/**
 * Driver Carrier Performance (V1 parity) — load-completion rating dialog.
 *
 * Mounted once at the app level. Opens when `useLoadRatingPromptStore` has a
 * pending prompt (set after a load is marked Completed — see
 * `maybeOpenLoadRatingPrompt`). Walks the load's ratable drivers one at a time;
 * for each, the operator stars every active question + optional notes, then
 * submits. On submit the server runs the blacklist engine, so a low rating
 * surfaces the warn / auto-blacklist toast (V1 parity) and recolours the grid.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import { Modal } from '../../core/components/ui/Modal';
import { Button } from '../../core/components/ui/Button';
import { useToast } from '../../core/components/ui/ToastProvider';
import {
  useLoadRatingPromptStore,
  useSubmitLoadDriverRatings,
} from '../../hooks/use-load-rating-prompt';

function StarInput({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          className="p-0.5"
          aria-label={t('loads.rating.starLabel', { count: i })}
        >
          <Star
            className={
              i <= (hover || value)
                ? 'h-6 w-6 fill-yellow-400 text-yellow-400'
                : 'h-6 w-6 text-gray-300'
            }
          />
        </button>
      ))}
    </div>
  );
}

export function LoadRatingPromptModal() {
  const { t } = useTranslation();
  const { prompt, close } = useLoadRatingPromptStore();
  const { showToast } = useToast();
  const submit = useSubmitLoadDriverRatings();

  const [driverIndex, setDriverIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState('');

  // Reset the per-driver form whenever the prompt opens or we advance drivers.
  useEffect(() => {
    setAnswers({});
    setNotes('');
  }, [prompt?.loadId, driverIndex]);

  // Reset to the first driver whenever a fresh prompt opens.
  useEffect(() => {
    setDriverIndex(0);
  }, [prompt?.loadId]);

  const currentDriver = prompt?.drivers[driverIndex];
  const allAnswered = useMemo(
    () => !!prompt && prompt.questions.every((q) => (answers[q.id] ?? 0) >= 1),
    [prompt, answers],
  );

  if (!prompt || !currentDriver) return null;

  const handleSubmit = async () => {
    if (!allAnswered) return;
    const result = await submit.mutateAsync({
      loadId: prompt.loadId,
      driverId: currentDriver.id,
      ratings: prompt.questions.map((q) => ({ questionId: q.id, rating: answers[q.id] })),
      notes: notes || undefined,
    });

    // V1 parity: surface the engine outcome for this driver.
    if (result.autoBlacklisted.length > 0) {
      const names = result.autoBlacklisted
        .map((d) => d.name ?? t('loads.rating.driverCarrierFallback'))
        .join(', ');
      showToast(t('loads.rating.toast.autoBlacklisted', { names }), 'warning');
    } else if (result.warned.length > 0) {
      const w = result.warned[0];
      const name = w?.name ?? t('loads.rating.driverCarrierFallback');
      showToast(
        typeof w?.rating === 'number'
          ? t('loads.rating.toast.warnedWithRating', { name, rating: w.rating })
          : t('loads.rating.toast.warned', { name }),
        'warning',
      );
    }

    // Advance to the next ratable driver, or close when done.
    if (driverIndex < prompt.drivers.length - 1) {
      setDriverIndex((i) => i + 1);
    } else {
      close();
    }
  };

  const baseTitle = t(
    currentDriver.driverType === 'carrier'
      ? 'loads.rating.titleCarrier'
      : 'loads.rating.titleDriver',
  );
  const title =
    prompt.drivers.length > 1
      ? t('loads.rating.titleWithProgress', {
          base: baseTitle,
          current: driverIndex + 1,
          total: prompt.drivers.length,
        })
      : baseTitle;

  return (
    <Modal
      open
      onClose={close}
      title={title}
      size="lg"
      closeOnBackdrop={false}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={close}>
            {t('loads.rating.skip')}
          </Button>
          <Button onClick={handleSubmit} loading={submit.isPending} disabled={!allAnswered}>
            {t('loads.actions.submit')}
          </Button>
        </div>
      }
    >
      <p className="text-sm text-gray-700 dark:text-gray-300">
        {prompt.displayId
          ? t('loads.rating.introWithLoad', {
              name: currentDriver.name ?? t('loads.rating.thisDriverFallback'),
              displayId: prompt.displayId,
            })
          : t('loads.rating.intro', {
              name: currentDriver.name ?? t('loads.rating.thisDriverFallback'),
            })}
      </p>

      <div className="mt-4 space-y-3">
        {prompt.questions.map((q) => (
          <div key={q.id} className="flex items-center justify-between gap-4">
            <span className="text-sm text-gray-800 dark:text-gray-200">
              {q.question} <span className="text-red-500" aria-hidden="true">*</span>
            </span>
            <StarInput
              value={answers[q.id] ?? 0}
              onChange={(n) => setAnswers((a) => ({ ...a, [q.id]: n }))}
            />
          </div>
        ))}

        <div>
          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">{t('loads.rating.notesLabel')}</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded border border-gray-300 dark:border-gray-600 bg-transparent px-2 py-1 text-sm"
          />
        </div>
      </div>
    </Modal>
  );
}
